/**
 * Сторож установления и удержания соединения.
 *
 * Два разных сюжета в одном файле, потому что оба про ICE:
 * 1. набор номера — не ждём полного сбора кандидатов, это 3-10 секунд на каждый звонок
 *    при пустой выгоде (спека §7.9: iceGatheringTimeout 2000 мс);
 * 2. разговор — мобильный интернет диспетчера моргает, поэтому 'disconnected' сам по себе
 *    не повод рвать звонок: даём паузу settings.telephony.ice_grace_sec (10 с),
 *    затем две попытки ICE restart, и только потом отбой с disposition='network_error'.
 */
import { systemScheduler } from './ports.js';
/**
 * Сколько ждём сбора кандидатов перед отправкой предложения.
 *
 * Спека §7.9 закладывала 2 секунды из соображения «не тормозить набор». На
 * практике этого не хватает: за две секунды браузер успевает собрать только
 * адреса своей локальной сети, а обращение к STUN за публичным адресом —
 * с разрешением имени и UDP-обменом — не укладывается.
 *
 * Прецедент 28.08.2026: предложение уходило с единственным кандидатом
 * `192.168.3.2` — это адрес ноутбука ВНУТРИ домашней сети диспетчера. Станция
 * отправляла звук туда и получала «Invalid argument», разговор шёл в тишине
 * с обеих сторон. Trickle ICE не спасает: станция его не поддерживает, и
 * кандидаты, собранные после отправки, до неё уже не доедут.
 *
 * Пять секунд — это ПОТОЛОК, а не задержка: ожидание обрывается сразу, как
 * только сбор завершился, а он завершается за сотни миллисекунд, когда STUN
 * доступен. Платим полными пятью секундами только там, где STUN недоступен
 * вовсе, — и там звонок всё равно состоится, просто по локальным кандидатам.
 */
export const ICE_GATHERING_TIMEOUT_MS = 5_000;
/** settings.telephony.ice_grace_sec = 10 */
export const ICE_GRACE_MS = 10_000;
export const ICE_MAX_RESTARTS = 2;
/**
 * Ждёт либо завершения сбора кандидатов, либо таймаута — что раньше.
 * Таймаут здесь нормальный исход, а не ошибка: половины кандидатов хватает для звонка.
 */
export function waitForIceGathering(pc, timeoutMs = ICE_GATHERING_TIMEOUT_MS, scheduler = systemScheduler) {
    if (pc.iceGatheringState === 'complete')
        return Promise.resolve();
    return new Promise((resolve) => {
        let done = false;
        let cancelTimer = null;
        const onChange = () => {
            if (pc.iceGatheringState === 'complete')
                finish();
        };
        const finish = () => {
            if (done)
                return;
            done = true;
            pc.removeEventListener('icegatheringstatechange', onChange);
            cancelTimer?.();
            resolve();
        };
        pc.addEventListener('icegatheringstatechange', onChange);
        cancelTimer = scheduler.schedule(finish, timeoutMs);
        // Планировщик может выполнить задачу сразу же — так делает подменённый
        // планировщик в тестах. Тогда finish уже отработал, и таймер надо снять здесь:
        // внутри finish переменная cancelTimer была ещё не присвоена
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (done)
            cancelTimer();
    });
}
export class IceWatchdog {
    #options;
    #scheduler;
    #pc = null;
    #detachListener = null;
    #cancelGrace = null;
    #restarts = 0;
    constructor(options) {
        this.#options = options;
        this.#scheduler = options.scheduler ?? systemScheduler;
    }
    get restarts() {
        return this.#restarts;
    }
    attach(pc) {
        if (this.#pc === pc)
            return;
        this.detach();
        this.#pc = pc;
        this.#restarts = 0;
        const listener = () => {
            this.#onIceStateChange(pc.iceConnectionState);
        };
        pc.addEventListener('iceconnectionstatechange', listener);
        this.#detachListener = () => {
            pc.removeEventListener('iceconnectionstatechange', listener);
        };
    }
    detach() {
        this.#cancelGrace?.();
        this.#cancelGrace = null;
        this.#detachListener?.();
        this.#detachListener = null;
        this.#pc = null;
    }
    #onIceStateChange(state) {
        if (state === 'connected' || state === 'completed') {
            if (this.#cancelGrace) {
                this.#cancelGrace();
                this.#cancelGrace = null;
                this.#options.onRecovered?.();
            }
            return;
        }
        if (state === 'disconnected') {
            this.#armGrace();
            return;
        }
        if (state === 'failed') {
            // failed самостоятельно не чинится, паузу не выдерживаем
            this.#cancelGrace?.();
            this.#cancelGrace = null;
            this.#attemptRestart();
        }
    }
    #armGrace() {
        if (this.#cancelGrace !== null)
            return;
        const graceMs = this.#options.graceMs ?? ICE_GRACE_MS;
        this.#cancelGrace = this.#scheduler.schedule(() => {
            this.#cancelGrace = null;
            const state = this.#pc?.iceConnectionState;
            if (state === 'connected' || state === 'completed')
                return;
            this.#attemptRestart();
        }, graceMs);
    }
    #attemptRestart() {
        const max = this.#options.maxRestarts ?? ICE_MAX_RESTARTS;
        if (this.#restarts >= max) {
            this.#options.onGiveUp();
            return;
        }
        this.#restarts += 1;
        void Promise.resolve(this.#options.onRestart(this.#restarts)).catch(() => {
            this.#options.onGiveUp();
        });
    }
}
//# sourceMappingURL=ice-watchdog.js.map